import tw from 'twin.macro'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CgPen } from 'react-icons/cg'
import { sortBy } from 'lodash'

import { STRIPE_SETUP } from 'modules/App/components/Header'
import { FormRefsControlProvider } from 'common/components/RefsControl/FormRefsControl/context'
import { useAuth } from 'modules/Auth/hooks/useAuth'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { useListenUserProfileQuery } from 'modules/Users/userApi'
import {
  useListenCheckoutPageQuery,
  useListenCheckoutPageSeoQuery,
  useListenCoachOrgSlugQuery,
  useListenPublishedCheckoutPageQuery,
  usePublishCheckoutPageMutation,
  useUpdateCheckoutPageSeoMutation,
} from '../pageBuilderApi'
import {
  useListenCouponsQuery,
  useListenOneTimeProductItemTypeQuery,
  useListenOneTimeProductQuery,
  useListenOneTimeProductSlugQuery,
  useListenProductCurrencyQuery,
  useListenProductPricingQuery,
  useListenStripeProductIdQuery,
  useUpdateOneTimeProductMutation,
} from 'modules/ProductInfo/productInfoApi'
import { useReadyForSaleTasks } from 'modules/ProductInfo/hooks/useReadyForSaleTasks'

import Loading from 'modules/App/components/Loading'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { CreateDialogBanner } from 'common/components/CreateDialogBanner/CreateDialogBanner'
import { buttonBase } from 'common/components/Button/Button'
import { ShareLinkMenu } from 'modules/ProductInfo/components/ShareLinkMenu/ShareLinkMenu'
import { SeoForm } from '../components/SeoForm/SeoForm'
import CheckoutPagePreview from '../components/PagePreview/CheckoutPagePreview'

export const PAGE_MAX_WIDTH = 1440

export default function CheckoutPageBuilder() {
  const navigate = useNavigate()
  const { productId } = useParams()

  const { createAlert } = useAlert()
  const { userId } = useAuth()
  const { data: profile } = useListenUserProfileQuery({ userId })
  const coachOrgId = profile?.coachOrgId || ''

  const [isPublishing, setIsPublishing] = useState(false)
  const [readyForSalePromptOpen, setReadyForSalePromptOpen] = useState(false)
  const [isEnablingReadyForSale, setIsEnablingReadyForSale] = useState(false)

  const { data: checkoutPage } = useListenCheckoutPageQuery({ coachOrgId, productId })
  const pageIsLoading = checkoutPage === undefined || checkoutPage?.isLoading
  const { data: plansData } = useListenProductPricingQuery({ coachOrgId })
  const productPlansLoading = plansData === undefined || plansData?.isLoading
  const productPlans = productPlansLoading || !plansData ? {} : plansData
  const sortedProductPlans = sortBy(productPlans, (plan) => plan.idx).filter(
    (plan) => Boolean(plan?.stripe_price_id) || Boolean(plan?.stripe_plan_id)
  )
  const { data: seoData } = useListenCheckoutPageSeoQuery({ coachOrgId, productId })
  const seoLoading = seoData === undefined || seoData?.isLoading
  const { data: coachOrgSlug } = useListenCoachOrgSlugQuery({ coachOrgId })
  const coachOrgSlugLoading = coachOrgSlug === undefined || coachOrgSlug?.isLoading
  const { data: coupons } = useListenCouponsQuery({ coachOrgId })
  const couponsLoading = coupons === undefined || coupons?.isLoading
  const { data: currency } = useListenProductCurrencyQuery({ coachOrgId })
  const currencyLoading = currency === undefined || currency?.isLoading
  const { data: stripeProductId } = useListenStripeProductIdQuery({ coachOrgId })
  const stripeProductIdLoading = stripeProductId === undefined || stripeProductId?.isLoading
  const { data: productSlug } = useListenOneTimeProductSlugQuery({ coachOrgId, productId })
  const productSlugLoading = productSlug === undefined || productSlug?.isLoading
  const { data: productItemType } = useListenOneTimeProductItemTypeQuery({ coachOrgId, productId })
  const productItemTypeLoading = productItemType === undefined || productItemType?.isLoading
  const { data: publishedCheckoutPage } = useListenPublishedCheckoutPageQuery({ coachOrgId, productId })
  const publishedCheckoutPageLoading = publishedCheckoutPage === undefined || publishedCheckoutPage?.isLoading

  const { data: oneTimeProductData } = useListenOneTimeProductQuery({ coachOrgId, productId }, { skip: !productId })
  const oneTimeProduct = oneTimeProductData === undefined || oneTimeProductData?.isLoading ? null : oneTimeProductData
  const { incompleteTasks, isLoading: readyForSaleTasksLoading } = useReadyForSaleTasks({
    coachOrgId,
    productId,
    product: oneTimeProduct,
    skip: !productId,
  })

  const [publishPage] = usePublishCheckoutPageMutation()
  const [updateSeo] = useUpdateCheckoutPageSeoMutation()
  const [updateOneTimeProduct] = useUpdateOneTimeProductMutation()

  const handlePublishPage = async () => {
    setIsPublishing(true)
    const { error } = await publishPage({ coachOrgId, productId })
    setIsPublishing(false)

    if (error) {
      createAlert({ text: 'Checkout page could not be published. Please try again.', type: 'danger' })
      return
    }

    createAlert({ text: 'Checkout page is published!', type: 'success' })

    const remainingTasks = incompleteTasks.filter((task) => task.id !== 'checkoutPage')
    if (
      productId &&
      oneTimeProduct?.readyForSale === false &&
      !readyForSaleTasksLoading &&
      remainingTasks.length === 0
    ) {
      setReadyForSalePromptOpen(true)
    }
  }

  const handleEnableReadyForSale = async () => {
    setIsEnablingReadyForSale(true)
    await updateOneTimeProduct({ coachOrgId, productId, product: { readyForSale: true } })
    setIsEnablingReadyForSale(false)
    createAlert({ text: 'Product is ready for sale!', type: 'success' })
  }

  useEffect(() => {
    if (!stripeProductIdLoading && !stripeProductId) {
      createAlert({ text: 'Please create Stripe product first', type: 'danger' })
      navigate(STRIPE_SETUP)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeProductIdLoading, stripeProductId])

  useEffect(() => {
    if (!coachOrgSlugLoading && !coachOrgSlug) {
      createAlert({ text: 'Please create your link first', type: 'danger' })
      navigate(STRIPE_SETUP)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachOrgSlugLoading, coachOrgSlug])

  if (
    pageIsLoading ||
    productPlansLoading ||
    seoLoading ||
    couponsLoading ||
    currencyLoading ||
    stripeProductIdLoading ||
    productSlugLoading ||
    productItemTypeLoading ||
    publishedCheckoutPageLoading
  ) {
    return (
      <div
        className='mt-12 max-w-[90rem] bg-white rounded-xl p-8 border-2 border-gray-200'
        css={tw`max-w-[${PAGE_MAX_WIDTH}]`}
      >
        <Loading showHeader={false} containerClasses='!bg-white' />
      </div>
    )
  }

  return (
    <div
      className='mt-12 max-w-[90rem] bg-white rounded-xl p-8 border-2 border-gray-200'
      css={tw`max-w-[${PAGE_MAX_WIDTH}]`}
    >
      <div className='flex flex-col bg-white'>
        <div className='w-full mx-auto'>
          <div className='flex items-center justify-between mb-5'>
            <div className='w-1/2'>
              <Dialog>
                <DialogTrigger css={[buttonBase]} className='text-tGray-med hover:text-tGreen'>
                  <CgPen className='w-4 h-4 mr-1' />
                  Edit SEO Settings
                </DialogTrigger>
                <DialogContent header='Edit SEO Settings'>
                  <FormRefsControlProvider>
                    <SeoForm
                      coachOrgId={coachOrgId}
                      productId={productId}
                      seo={seoData}
                      updateSeo={updateSeo}
                      uploadLocation={productId ? `oneTimeProducts/${productId}/siteCheckout` : 'siteCheckout'}
                      uploadType={productId ? 'one-time-seo-checkout-image' : 'seo-checkout-image'}
                    />
                  </FormRefsControlProvider>
                </DialogContent>
              </Dialog>
            </div>
            <ShareLinkMenu
              coachSlug={coachOrgSlug}
              pagePath={productId ? `/product/${productSlug || 'your-slug'}/checkout` : '/checkout'}
              blockedReason={publishedCheckoutPage ? null : 'Publish this page before sharing the link'}
              variant='button'
            />
          </div>
          <div className='flex flex-1 flex-col items-center relative'>
            <FormRefsControlProvider>
              <CheckoutPagePreview
                coachOrgId={coachOrgId}
                page={checkoutPage}
                productPlans={sortedProductPlans}
                coupons={coupons}
                handlePublishPage={handlePublishPage}
                isPublishing={isPublishing}
              />
            </FormRefsControlProvider>
          </div>
        </div>
      </div>
      <Dialog open={readyForSalePromptOpen} setOpen={setReadyForSalePromptOpen}>
        <DialogContent>
          <CreateDialogBanner
            titleText='Mark as ready for sale?'
            text='All steps are complete. Turn on "Ready for sale" to make this product visible and purchasable on your storefront.'
            createBtnText='Turn on'
            cancelBtnText='Not now'
            handleCreate={handleEnableReadyForSale}
            loading={isEnablingReadyForSale}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
